import type {ClothingItemEditData} from '../components/AddClothingItemModal';

/** Результат AI-распознавания вещи: поля, предложенные моделью для карточки. */
export interface AiResult {
    name?: string;
    description?: string;
    categoryId?: string;
    brandId?: string;
    sizeId?: string;
    conditionId?: string;
    colorIds?: string[];
    layerId?: string;
    warmthLevelId?: string;
    priceRub?: number;
    waterProof?: boolean;
    purchaseDate?: string;
    disposalDate?: string;
    thrownAway?: boolean;
    seasonIds?: string[];
    materialIds?: string[];
    occasionIds?: string[];
    styleIds?: string[];
    weatherSuitabilityIds?: string[];
    defectIds?: string[];
    /** recognition-v5: найденные недостатки свободным текстом — показываются на ревью. */
    detectedFlaws?: string[];
    printTypeId?: string;
    fitId?: string;
    formalityId?: string;
}

/** Заявка AI-распознавания вещи по фото. */
export interface AiRecognitionRequest {
    id: string;
    status: string;
    model?: string;
    userDescription?: string;
    systemPrompt?: string;
    userPrompt?: string;
    errorMessage?: string;
    clothingItemId?: string;
    sourceItemId?: string;
    /** Судьба фото заявки при подтверждении повторного анализа: KEEP|APPEND|REPLACE. */
    photoMode?: string;
    photoKeys: string[];
    aiResult?: AiResult;
    createdAt: string;
    updatedAt?: string;
}

/** Человекочитаемые статусы заявки. */
export const RECOGNITION_STATUS_LABELS: Record<string, string> = {
    NEW: 'Новый',
    HOLD: 'Ожидает ключ',
    PENDING: 'В очереди',
    PROCESSING: 'Распознаётся',
    REVIEW: 'На проверке',
    DONE: 'Готово',
    ERROR: 'Ошибка',
    CANCELED: 'Отменён',
};

/** Заявку можно проверить и сохранить как вещь. */
export const isReviewable = (status?: string): boolean => status === 'REVIEW';

/** Заявка ещё в работе — результат появится позже. */
export const isInProgress = (status?: string): boolean =>
    ['NEW', 'HOLD', 'PENDING', 'PROCESSING'].includes(status ?? '');

/**
 * Разбирает ссылку действия стилиста вида `AI_RECOGNITION:<uuid>`.
 * Используется чатом, чтобы открыть карточку проверки прямо в диалоге.
 */
export function parseRecognitionRef(resultRef?: string | null): string | null {
    if (!resultRef) return null;
    const [kind, id] = resultRef.split(':');
    return kind === 'AI_RECOGNITION' && id ? id : null;
}

/**
 * Данные для формы проверки: поля, предложенные моделью, плюс фото заявки.
 * Возвращает `undefined`, если проверять нечего (результата нет или заявка не в REVIEW) —
 * тогда форму открывать нельзя.
 */
export function toEditItem(req: AiRecognitionRequest | null): ClothingItemEditData | undefined {
    if (!req?.aiResult || !isReviewable(req.status)) return undefined;
    const a = req.aiResult;
    return {
        id: req.id,
        name: a.name || 'Без названия',
        description: a.description ?? null,
        categoryId: a.categoryId ?? null,
        brandId: a.brandId ?? null,
        sizeId: a.sizeId ?? null,
        conditionId: a.conditionId ?? null,
        colorIds: a.colorIds ?? null,
        layerIds: a.layerId ? [a.layerId] : null,
        warmthLevelId: a.warmthLevelId ?? null,
        priceRub: a.priceRub ?? null,
        waterProof: a.waterProof ?? null,
        purchaseDate: a.purchaseDate ?? null,
        disposalDate: a.disposalDate ?? null,
        thrownAway: a.thrownAway ?? null,
        seasonIds: a.seasonIds ?? null,
        materialIds: a.materialIds ?? null,
        occasionIds: a.occasionIds ?? null,
        styleIds: a.styleIds ?? null,
        weatherSuitabilityIds: a.weatherSuitabilityIds ?? null,
        defectIds: a.defectIds ?? null,
        printTypeId: a.printTypeId ?? null,
        fitId: a.fitId ?? null,
        formalityId: a.formalityId ?? null,
        photoUrls: req.photoKeys,
    };
}

/**
 * Разбирает ссылку действия стилиста вида `TRIP:<uuid>` (CTHS-212).
 * По ней чат рисует карточку поездки с переходом на её страницу.
 */
export function parseTripRef(resultRef?: string | null): string | null {
    if (!resultRef) return null;
    const [kind, id] = resultRef.split(':');
    return kind === 'TRIP' && id ? id : null;
}
